/**
 * Resolves what the player should play.
 *
 *   ?code=XXXX          share code → Firestore shareCodes/{code} → projects/{id};
 *                       if missing, calls the importProject Cloud Function.
 *   ?project=<id>       Firestore projects/{id} directly
 *                       (&test=<slideId>: from the editor's Play, starting on that slide).
 *   ?demo=<lang>/<id>   static JSON under /demos (dev & QA, no Firebase).
 */
import { parseProject, type Project } from "@makeit/schema";

export type ProjectSource =
  | { kind: "code"; code: string }
  /** `testFrom`: played from the editor to try that slide (Origin.editor), see Player */
  | { kind: "project"; projectId: string; testFrom?: number }
  | { kind: "demo"; lang: string; id: string; testFrom?: number }
  /** dev only: the editor's local test project, played as it is in memory */
  | { kind: "inline"; project: Project; testFrom?: number };

export function sourceFromLocation(search: string = window.location.search): ProjectSource | null {
  const q = new URLSearchParams(search);
  const code = q.get("code")?.trim();
  if (code) return { kind: "code", code };
  const projectId = q.get("project")?.trim();
  const test = Number(q.get("test") ?? NaN);
  if (projectId) return Number.isInteger(test) ? { kind: "project", projectId, testFrom: test } : { kind: "project", projectId };
  const demo = q.get("demo")?.trim();
  if (demo) {
    const [lang, id] = demo.split("/");
    if (lang && id) return Number.isInteger(test) ? { kind: "demo", lang, id, testFrom: test } : { kind: "demo", lang, id };
  }
  return null;
}

export class LoadError extends Error {
  constructor(
    message: string,
    public readonly reason: "not-found" | "network" | "invalid" | "server",
  ) {
    super(message);
    this.name = "LoadError";
  }
}

export async function loadProject(source: ProjectSource): Promise<Project> {
  switch (source.kind) {
    case "demo":
      return loadDemo(source.lang, source.id);
    case "project":
      return loadFromFirestore(source.projectId);
    case "code":
      return loadByCode(source.code);
    case "inline":
      return source.project;
  }
}

async function loadDemo(lang: string, id: string): Promise<Project> {
  const res = await fetch(`/demos/${encodeURIComponent(lang)}/${encodeURIComponent(id)}.json`);
  if (res.status === 404) throw new LoadError("Demo not found", "not-found");
  if (!res.ok) throw new LoadError(`HTTP ${res.status}`, "network");
  return validate(await res.json());
}

async function loadFromFirestore(projectId: string): Promise<Project> {
  const { getFirestore, doc, getDoc } = await import("firebase/firestore");
  const { getApp } = await import("../firebase");
  const db = getFirestore(getApp());
  const snap = await getDoc(doc(db, "projects", projectId));
  if (!snap.exists()) throw new LoadError("Project not found", "not-found");
  const { meta: _meta, ...data } = snap.data() as Record<string, unknown>;
  return validate(data);
}

async function loadByCode(code: string): Promise<Project> {
  const { getFirestore, doc, getDoc } = await import("firebase/firestore");
  const { getApp } = await import("../firebase");
  const db = getFirestore(getApp());

  // Fast path: already imported.
  const codeSnap = await getDoc(doc(db, "shareCodes", code));
  if (codeSnap.exists()) {
    const projectId = codeSnap.get("projectId") as string;
    try {
      return await loadFromFirestore(projectId);
    } catch (err) {
      if (!(err instanceof LoadError && err.reason === "not-found")) throw err;
    }
  }

  // Slow path: ask the Cloud Function to import it from the legacy backend.
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const { FUNCTIONS_REGION } = await import("../firebase");
  const fns = getFunctions(getApp(), FUNCTIONS_REGION);
  const importProject = httpsCallable<{ code: string }, { projectId: string; imported: boolean }>(fns, "importProject");
  let projectId: string;
  try {
    const result = await importProject({ code });
    projectId = result.data.projectId;
  } catch (err) {
    const e = err as { code?: string; message?: string };
    if (e.code === "functions/not-found") throw new LoadError("No project with that code", "not-found");
    if (e.code === "functions/unavailable") throw new LoadError("Project server unavailable", "server");
    throw new LoadError(e.message ?? "Import failed", "server");
  }
  return loadFromFirestore(projectId);
}

function validate(data: unknown): Project {
  try {
    return parseProject(data);
  } catch (err) {
    throw new LoadError(`Invalid project data: ${(err as Error).message}`, "invalid");
  }
}
